<template>
  <component
    :is="as"
    class="grade-variation-chip"
    :class="[gradeClass, { clickable, 'has-dropdown': dropdown, 'is-profile': profile, variation: Boolean(variation), 'no-color': noColor }]"
    v-bind="$attrs"
    v-on="$listeners"
    v-tooltip="dropdown && variation && { content: variation, delay: 300, placement: 'top' }"
  >
    <span>{{ grade || variation }}</span>

    <icon
      v-if="dropdown"
      name="arrow_drop_down"
      class="icon"
    />
  </component>
</template>

<script>
import Icon from './Icon';

export default {
  components: {
    Icon,
  },

  props: {
    as: {
      type: String,
      options: ['a', 'nuxt-link', 'router-link', 'button', 'span'],
      default: 'span',
    },
    grade: String,
    variation: String,
    dropdown: Boolean,
    profile: Boolean,
    noColor: Boolean,
  },

  computed: {
    clickable() {
      return this.as !== 'span';
    },

    gradeClass() {
      if (!this.grade || typeof this.grade.toLowerCase !== 'function') {
        return '';
      }

      let classReturn = this.grade
        .toLowerCase()
        .replace('/auto 10', '')
        .replace('/auto 9', '')
        .replace('/auto 8', '')
        .replace(' ', '-')
        .replace('.', '');

      if (!classReturn.includes('-')) {
        classReturn = `grade-${classReturn}`;
      }

      return classReturn;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@cardladder/shared/scss/variables.scss';

.grade-variation-chip {
  padding: 6px 10px;
  border: solid 1px var(--grade-color);
  background-color: var(--grade-color);
  color: $color-white;
  border-radius: $border-radius;
  font-size: 14px;
  font-weight: bold;
  align-items: center;
  line-height: 1.5;
  background-color: transparent;
  color: var(--grade-color);

  &.variation {
    border: solid 1px var(--theme-input-border);
    color: var(--theme-color);
  }

  &:not(.is-profile) {
    @include ellipsis;

    span {
      @include ellipsis;
    }
  }

  &.is-profile {
    white-space: break-spaces;
  }

  &.has-dropdown {
    position: static;
    margin: 0;
    padding-right: $gp * .25;

    .icon {
      margin-left: $gp * .25;
      font-size: 16px;
      position: relative;
      top: 3px;
    }
  }

  &.has-dropdown:not(.is-profile) {
    span {
      max-width: 170px;
    }
  }

  &.clickable {
    cursor: pointer;
  }

  @media($medium-down) {
    span {
      max-width: 170px;
    }
  }
}

.psa-10,
.bgs-10,
.sgc-10,
.bvg-10,
.hga-10,
.csg-10,
.cgc-10 {
  --grade-color: #{$color-grade-10};
}

.psa-9,
.bgs-9,
.sgc-9,
.bvg-9,
.hga-9,
.csg-9,
.cgc-9,
.psa-95,
.bgs-95,
.sgc-95,
.bvg-95,
.hga-95,
.csg-95,
.cgc-95 {
  --grade-color: #{$color-grade-9};
}

.psa-8,
.bgs-8,
.sgc-8,
.bvg-8,
.hga-8,
.csg-8,
.cgc-8,
.psa-85,
.bgs-85,
.sgc-85,
.bvg-85,
.hga-85,
.csg-85,
.cgc-85 {
  --grade-color: #{$color-grade-8};
}

.psa-7,
.bgs-7,
.sgc-7,
.bvg-7,
.hga-7,
.csg-7,
.cgc-7,
.psa-75,
.bgs-75,
.sgc-75,
.bvg-75,
.hga-75,
.csg-75,
.cgc-75 {
  --grade-color: #{$color-grade-7};
}

.psa-6,
.bgs-6,
.sgc-6,
.bvg-6,
.hga-6,
.csg-6,
.cgc-6,
.psa-65,
.bgs-65,
.sgc-65,
.bvg-65,
.hga-65,
.csg-65,
.cgc-65 {
  --grade-color: #{$color-grade-6};
}

.psa-5,
.bgs-5,
.sgc-5,
.bvg-5,
.hga-5,
.csg-5,
.cgc-5,
.psa-55,
.bgs-55,
.sgc-55,
.bvg-55,
.hga-55,
.csg-55,
.cgc-55 {
  --grade-color: #{$color-grade-5};
}

.psa-4,
.bgs-4,
.sgc-4,
.bvg-4,
.hga-4,
.csg-4,
.cgc-4,
.psa-45,
.bgs-45,
.sgc-45,
.bvg-45,
.hga-45,
.csg-45,
.cgc-45 {
  --grade-color: #{$color-grade-4};
}

.psa-3,
.bgs-3,
.sgc-3,
.bvg-3,
.hga-3,
.csg-3,
.cgc-3,
.psa-35,
.bgs-35,
.sgc-35,
.bvg-35,
.hga-35,
.csg-35,
.cgc-35 {
  --grade-color: #{$color-grade-3};
}

.psa-2,
.bgs-2,
.sgc-2,
.bvg-2,
.hga-2,
.csg-2,
.cgc-2,
.psa-25,
.bgs-25,
.sgc-25,
.bvg-25,
.hga-25,
.csg-25,
.cgc-25 {
  --grade-color: #{$color-grade-2};
}

.psa-1,
.bgs-1,
.sgc-1,
.bvg-1,
.hga-1,
.csg-1,
.cgc-1,
.psa-15,
.bgs-15,
.sgc-15,
.bvg-15,
.hga-15,
.csg-15,
.cgc-15 {
  --grade-color: #{$color-grade-1};
}

.raw {
  --grade-color: #{$color-grade-raw};
}

.psa-auth,
.bgs-auth,
.sgc-auth {
  --grade-color: var(--theme-color) !important;
}

.sealed,
.encased {
  --grade-color: #{$color-grade-sealed};
}

.no-color {
  --grade-color: var(--theme-color) !important;
}
</style>
